const LATIN = /[\p{Script=Latin}\p{Nd}]/u;
const HANGUL = /\p{Script=Hangul}/u;
const KANA = /[\p{Script=Hiragana}\p{Script=Katakana}]/u;
const HAN = /\p{Script=Han}/u;
const MARK = /\p{M}/u;
const GLOSSARY_BLOCK = /:::glossary\s*\n([\s\S]*?):::/g;
const DATA_GLOSS = /<span\b[^>]*\bdata-gloss=(["'])([^"']+)\1[^>]*>([\s\S]*?)<\/span>/gi;

export const DRAG_LOOKUP_MIN_DISTANCE = 8;

export function normalizeWord(word) {
  return String(word || '').normalize('NFC').trim().toLowerCase();
}

export function isWordChar(ch) {
  return LATIN.test(ch) || HANGUL.test(ch) || KANA.test(ch) || HAN.test(ch) || MARK.test(ch);
}

function scriptKind(ch) {
  if (HANGUL.test(ch)) return 'hangul';
  if (KANA.test(ch)) return 'kana';
  if (HAN.test(ch)) return 'han';
  if (LATIN.test(ch) || MARK.test(ch)) return 'latin';
  return '';
}

function isJoiner(ch, kind) {
  return kind === 'latin' && (ch === "'" || ch === '’' || ch === '-');
}

export function expandWord(text, index) {
  if (!text) return null;
  const i = index;
  if (i < 0 || i >= text.length || !isWordChar(text[i])) return null;
  const kind = scriptKind(text[i]);
  if (!kind) return null;
  if (kind === 'han') {
    return { start: i, end: i + 1, word: text[i] };
  }
  let start = i;
  let end = i + 1;
  while (start > 0) {
    const prev = text[start - 1];
    if (scriptKind(prev) === kind || MARK.test(prev) || isJoiner(prev, kind)) start -= 1;
    else break;
  }
  while (end < text.length) {
    const next = text[end];
    if (scriptKind(next) === kind || MARK.test(next) || isJoiner(next, kind)) end += 1;
    else break;
  }
  while (start < end && isJoiner(text[start], kind)) start += 1;
  while (end > start && isJoiner(text[end - 1], kind)) end -= 1;
  const word = text.slice(start, end);
  if (!word || ![...word].some((ch) => isWordChar(ch))) return null;
  return { start, end, word };
}

export function isDragLookup(from, to, min = DRAG_LOOKUP_MIN_DISTANCE) {
  const dx = Number(to?.x) - Number(from?.x);
  const dy = Number(to?.y) - Number(from?.y);
  return Math.hypot(dx, dy) >= min;
}

export function isLookupCancelKey(key) {
  return key === 'Escape' || key === 'Esc';
}

export function clearTextSelection(selection) {
  const current = selection || (typeof window !== 'undefined' ? window.getSelection() : null);
  if (current && typeof current.removeAllRanges === 'function') {
    current.removeAllRanges();
  }
}

export function isPlainCodeToken(text) {
  const value = String(text || '').trim();
  return Boolean(value) && /^[\x00-\x7F]+$/.test(value);
}

export function skipTranslateElement(el, text) {
  if (!el || typeof el.closest !== 'function') return true;
  if (el.closest('pre, script, style, noscript, svg, .ugot-lecture-chrome')) return true;
  if (el.closest('code') && isPlainCodeToken(text)) return true;
  return false;
}

export function lookupHost(target, roots) {
  if (!target || !Array.isArray(roots)) return null;
  const node = target.nodeType === 1 ? target : target.parentElement;
  if (!node) return null;
  for (const root of roots) {
    if (root && typeof root.contains === 'function' && root.contains(node)) return root;
  }
  return null;
}

function parseGlossaryLines(text, glossary) {
  for (const line of String(text || '').split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const match = trimmed.match(/^(.+?)\s*[:|=]\s*(.+)$/);
    if (!match) continue;
    const key = normalizeWord(match[1]);
    const gloss = match[2].trim();
    if (key && gloss) glossary.set(key, gloss);
  }
}

function stripTags(html) {
  return String(html || '').replace(/<[^>]+>/g, '');
}

export function extractGlossary(markdown) {
  const src = String(markdown || '');
  const glossary = new Map();
  const body = src.replace(GLOSSARY_BLOCK, (_, inner) => {
    parseGlossaryLines(inner, glossary);
    return '';
  });
  DATA_GLOSS.lastIndex = 0;
  let span = DATA_GLOSS.exec(src);
  while (span) {
    const key = normalizeWord(stripTags(span[3]));
    const gloss = String(span[2] || '').trim();
    if (key && gloss) glossary.set(key, gloss);
    span = DATA_GLOSS.exec(src);
  }
  return { body: body.trim(), glossary };
}

function reverseGloss(query, glossary) {
  const needle = normalizeWord(query);
  if (!needle) return null;
  for (const [word, gloss] of glossary) {
    if (normalizeWord(gloss) === needle) return { word: String(query).trim(), gloss: word };
  }
  return null;
}

export function lookupGloss(query, glossary) {
  if (!glossary || glossary.size === 0) return null;
  const raw = String(query || '').trim();
  if (!raw) return null;
  const exact = glossary.get(normalizeWord(raw));
  if (exact) return { word: raw, gloss: exact };
  const first = expandWord(raw, 0);
  if (first) {
    const hit = glossary.get(normalizeWord(first.word));
    if (hit) return { word: first.word, gloss: hit };
  }
  const reversed = reverseGloss(raw, glossary);
  if (reversed) return reversed;
  if (!first) return null;
  return reverseGloss(first.word, glossary);
}

export function authoredPairs(text, glossary) {
  const src = String(text || '');
  if (!src || !glossary || glossary.size === 0) return [];
  const needles = [...glossary.entries()]
    .map(([word, gloss]) => ({ word, gloss }))
    .sort((a, b) => b.word.length - a.word.length);
  const used = new Array(src.length).fill(false);
  const ranges = [];
  const lower = src.normalize('NFC').toLowerCase();
  const whole = normalizeWord(src);
  for (const needle of needles) {
    if (needle.word === whole) continue;
    let from = 0;
    while (from < lower.length) {
      const at = lower.indexOf(needle.word, from);
      if (at === -1) break;
      const end = at + needle.word.length;
      let taken = false;
      for (let i = at; i < end; i += 1) {
        if (used[i]) {
          taken = true;
          break;
        }
      }
      if (!taken) {
        for (let i = at; i < end; i += 1) used[i] = true;
        ranges.push({
          start: at,
          end,
          word: src.slice(at, end),
          gloss: needle.gloss,
        });
      }
      from = at + 1;
    }
  }
  ranges.sort((a, b) => a.start - b.start);
  return ranges.map(({ word, gloss }) => ({ word, gloss }));
}

export function applyGlossary(text, glossary) {
  const src = String(text || '');
  return glossary?.get(normalizeWord(src)) || '';
}

function visibleParagraphs(body) {
  return body
    .replace(/```[\s\S]*?```/g, '')
    .split(/\n{2,}/)
    .map((part) => part.replace(/^#{1,6}\s+/, '').replace(/^\s*>\s?/gm, '').replace(/[*_`]/g, '').trim())
    .filter(Boolean);
}

export function splitBlocks(markdown) {
  const { body, glossary } = extractGlossary(markdown);
  return visibleParagraphs(body).map((source) => {
    const sentence = glossary.get(normalizeWord(source)) || '';
    const pairs = authoredPairs(source, glossary);
    return {
      source,
      sentence,
      pairs,
      gloss: sentence,
    };
  });
}

function languageCodes(list) {
  return [...new Set(
    (Array.isArray(list) ? list : [])
      .map((code) => String(code || '').trim().toLowerCase().split(/[-_]/)[0])
      .filter(Boolean),
  )];
}

export function resolveSplitMode(options = {}) {
  const targetLang = String(options.targetLang || '').trim().toLowerCase().split(/[-_]/)[0];
  const localizedMap = options.localizedMarkdown && typeof options.localizedMarkdown === 'object'
    ? options.localizedMarkdown
    : {};
  const localized = targetLang ? String(localizedMap[targetLang] || '').trim() : '';
  if (localized) return { mode: 'localized', markdown: localized };
  const blocks = Array.isArray(options.blocks)
    ? options.blocks
    : splitBlocks(options.markdown || '');
  if (blocks.some((block) => block.sentence)) return { mode: 'authored' };
  const supported = languageCodes(options.supportedLanguages);
  const filled = languageCodes(
    Object.keys(localizedMap).filter((key) => String(localizedMap[key] || '').trim()),
  );
  if (supported.length > 1 || filled.length > 1) return { mode: 'missing' };
  return { mode: 'google' };
}

export function shouldUseGoogleTranslate(options) {
  return resolveSplitMode(options).mode === 'google';
}
